/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts"
import { FileText, Download, Calendar, TrendingUp, Shield, Clock, CheckCircle } from "lucide-react"
import { ReportViewPopup } from "@/components/report-view-popup"

interface ReportData {
  id: string
  title: string
  type: string
  generatedDate: string
  generatedBy: string
  status: "completed" | "pending" | "failed"
  data: any
}

// Dummy report data
const crimeStatsData = [
  { caseType: "Theft", count: 45, percentage: 35 },
  { caseType: "Assault", count: 28, percentage: 22 },
  { caseType: "Fraud", count: 32, percentage: 25 },
  { caseType: "Domestic Violence", count: 23, percentage: 18 },
]

const monthlyTrendsData = [
  { month: "Jan", cases: 65, solved: 58 },
  { month: "Feb", cases: 72, solved: 65 },
  { month: "Mar", cases: 68, solved: 61 },
  { month: "Apr", cases: 78, solved: 70 },
  { month: "May", cases: 85, solved: 76 },
  { month: "Jun", cases: 92, solved: 83 },
]

const policeStationData = [
  { station: "Bandra", cases: 45, officers: 12, solved: 38 },
  { station: "Andheri", cases: 52, officers: 15, solved: 44 },
  { station: "Borivali", cases: 38, officers: 10, solved: 32 },
  { station: "Malad", cases: 41, officers: 11, solved: 35 },
]

const officerPerformanceData = [
  { name: "Inspector Sharma", cases: 15, solved: 13, efficiency: 87 },
  { name: "Sub-Inspector Patel", cases: 12, solved: 10, efficiency: 83 },
  { name: "ASI Kumar", cases: 18, solved: 14, efficiency: 78 },
  { name: "Inspector Verma", cases: 14, solved: 12, efficiency: 86 },
]

const initialReports: ReportData[] = [
  {
    id: "1",
    title: "Monthly Crime Statistics - January 2024",
    type: "Crime Statistics",
    generatedDate: "2024-01-31T10:00:00Z",
    generatedBy: "Inspector Sharma",
    status: "completed",
    data: { crimeStats: crimeStatsData, summary: "Total cases: 128, Solved: 115" },
  },
  {
    id: "2",
    title: "Officer Performance Report - Q1 2024",
    type: "Officer Performance",
    generatedDate: "2024-01-30T14:30:00Z",
    generatedBy: "Deputy Commissioner",
    status: "completed",
    data: { officers: officerPerformanceData, avgEfficiency: 83.5 },
  },
  {
    id: "3",
    title: "Police Station Analysis - Mumbai District",
    type: "Station Analysis",
    generatedDate: "2024-01-29T09:15:00Z",
    generatedBy: "Inspector Verma",
    status: "completed",
    data: { stations: policeStationData, totalCases: 176 },
  },
]

const COLORS = ["#000000", "#404040", "#808080", "#C0C0C0"]

export default function ReportsOld() {
  const [reports, setReports] = useState<ReportData[]>(initialReports)
  const [selectedReportType, setSelectedReportType] = useState("")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [selectedStation, setSelectedStation] = useState("")
  const [viewingReport, setViewingReport] = useState<ReportData | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)

  const reportTypes = [
    "Crime Statistics",
    "Officer Performance",
    "Station Analysis",
    "Monthly Summary",
    "Case Status",
    "Time Analysis",
  ]

  const policeStations = ["All Stations", "Bandra", "Andheri", "Borivali", "Malad"]

  const handleGenerateReport = async () => {
    if (!selectedReportType) return

    setIsGenerating(true)

    // Simulate report generation
    await new Promise((resolve) => setTimeout(resolve, 2000))

    const newReport: ReportData = {
      id: Date.now().toString(),
      title: `${selectedReportType} - ${new Date().toLocaleDateString("en-IN")}`,
      type: selectedReportType,
      generatedDate: new Date().toISOString(),
      generatedBy: "Current User",
      status: "completed",
      data: {
        crimeStats: crimeStatsData,
        officers: officerPerformanceData,
        stations: policeStationData,
        trends: monthlyTrendsData,
      },
    }

    setReports([newReport, ...reports])
    setIsGenerating(false)
  }

  const handleViewReport = (report: ReportData) => {
    setViewingReport(report)
  }

  const handleExportReport = (reportId: string) => {
    // Simulate export functionality
    console.log("Exporting report:", reportId)
    // In a real app, this would trigger a PDF download
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      completed: { color: "bg-green-100 text-green-800", text: "Completed" },
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      failed: { color: "bg-red-100 text-red-800", text: "Failed" },
    }

    const config = statusConfig[status as keyof typeof statusConfig]
    return <Badge className={`${config.color} hover:${config.color}`}>{config.text}</Badge>
  }

  return (
    <div className="min-h-screen bg-gradient-to-r from-[#0d2846]/3 via-[#e2af58]/10 to-[#0d2846]/3 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-black">Reports</h1>
            <p className="text-gray-600 mt-1">Generate and manage police department reports</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-gray-600" />
                <div>
                  <div className="text-2xl font-bold text-black">{reports.length}</div>
                  <p className="text-sm text-gray-600">Total Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {reports.filter((r) => r.status === "completed").length}
                  </div>
                  <p className="text-sm text-gray-600">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <div>
                  <div className="text-2xl font-bold text-yellow-600">
                    {reports.filter((r) => r.status === "pending").length}
                  </div>
                  <p className="text-sm text-gray-600">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="text-2xl font-bold text-blue-600">{reportTypes.length}</div>
                  <p className="text-sm text-gray-600">Report Types</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="generate" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 bg-gray-100">
            <TabsTrigger value="generate" className="data-[state=active]:bg-white">
              Generate Report
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-white">
              Analytics
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-white">
              Report History
            </TabsTrigger>
          </TabsList>

          {/* Generate Report Tab */}
          <TabsContent value="generate">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">Generate New Report</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">Report Type</Label>
                    <Select value={selectedReportType} onValueChange={setSelectedReportType}>
                      <SelectTrigger className="border-gray-300">
                        <SelectValue placeholder="Select report type" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200">
                        {reportTypes.map((type) => (
                          <SelectItem key={type} value={type} className="hover:bg-gray-50">
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">From Date</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">To Date</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="border-gray-300"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">Police Station</Label>
                    <Select value={selectedStation} onValueChange={setSelectedStation}>
                      <SelectTrigger className="border-gray-300">
                        <SelectValue placeholder="Select station" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200">
                        {policeStations.map((station) => (
                          <SelectItem key={station} value={station} className="hover:bg-gray-50">
                            {station}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleGenerateReport}
                    disabled={!selectedReportType || isGenerating}
                    className="bg-gradient-to-r from-[#0d2846] to-[#0d2846]/90 hover:from-[#0d2846]/90 hover:to-[#0d2846] cursor-pointer text-white"
                  >
                    {isGenerating ? "Generating..." : "Generate Report"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Crime Statistics Chart */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Crime Statistics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={crimeStatsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="caseType" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Bar dataKey="count" fill="#000000" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Case Distribution Pie Chart */}
              <Card className="border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-lg text-black flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Case Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={crimeStatsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ caseType, percentage }) => `${caseType}: ${percentage}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {crimeStatsData.map((entry, index) => (
                          <Cell key={`cell-${index}-${entry}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Monthly Trends */}
              <Card className="border border-gray-200 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg text-black flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Monthly Trends
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={monthlyTrendsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="month" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                        }}
                      />
                      <Line type="monotone" dataKey="cases" stroke="#000000" strokeWidth={2} name="Total Cases" />
                      <Line type="monotone" dataKey="solved" stroke="#666666" strokeWidth={2} name="Solved Cases" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Report History Tab */}
          <TabsContent value="history">
            <Card className="border border-gray-200">
              <CardHeader>
                <CardTitle className="text-lg text-black">Report History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border border-gray-200 rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-black font-semibold">Report Title</TableHead>
                        <TableHead className="text-black font-semibold">Type</TableHead>
                        <TableHead className="text-black font-semibold">Generated Date</TableHead>
                        <TableHead className="text-black font-semibold">Generated By</TableHead>
                        <TableHead className="text-black font-semibold">Status</TableHead>
                        <TableHead className="text-black font-semibold">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reports.map((report) => (
                        <TableRow key={report.id} className="hover:bg-gray-50">
                          <TableCell className="font-medium text-black">{report.title}</TableCell>
                          <TableCell className="text-gray-600">{report.type}</TableCell>
                          <TableCell className="text-gray-600">
                            {new Date(report.generatedDate)?.toLocaleString() ?? "-"}
                          </TableCell>
                          <TableCell className="text-gray-600">{report.generatedBy}</TableCell>
                          <TableCell>{getStatusBadge(report.status)}</TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewReport(report)}
                                className="border-gray-300 hover:bg-gray-50"
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleExportReport(report.id)}
                                className="border-gray-300 hover:bg-gray-50"
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Export
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Report View Popup */}
      <ReportViewPopup open={!!viewingReport} onOpenChange={() => setViewingReport(null)} report={viewingReport} />
    </div>
  )
}
